import { Mountain, Droplets, ThermometerSun, MapPin, Quote, Calendar, Heart } from "lucide-react";
import iconCabinet from "@/assets/icon-cabinet.png";
import iconBalcony from "@/assets/icon-balcony.png";
import iconCleaning from "@/assets/icon-cleaning.png";
import iconWifi from "@/assets/icon-wifi.png";
import iconSpa from "@/assets/icon-spa.png";
import iconBathroom from "@/assets/icon-bathroom.png";
import hotelHistoric from "@/assets/hotel-historic-1918.png";
import galleryEntrance from "@/assets/gallery-entrance-sign.png";
import galleryPickup from "@/assets/gallery-pickup-area.png";
import galleryLobby from "@/assets/gallery-lobby-decor.png";
import galleryRestaurant from "@/assets/gallery-restaurant-interior.png";
import galleryAerial from "@/assets/gallery-aerial-view.png";
import galleryRiver from "@/assets/gallery-river-view.png";
import galleryTerrace from "@/assets/gallery-terrace.png";
import galleryBuffet from "@/assets/gallery-breakfast-buffet.png";
import galleryBalcony from "@/assets/gallery-balcony-view.png";
import galleryFacade from "@/assets/gallery-facade.png";
import hotelExterior2 from "@/assets/hotel-exterior-2.png";
import { useLanguage } from "@/i18n/LanguageContext";
import { useCmsContent } from "@/hooks/useCmsContent";

const About = () => {
  const { t } = useLanguage();
  const { get } = useCmsContent();

  const galleryImages = [
    { src: galleryFacade, alt: "Hotel", span: "col-span-2 row-span-2" },
    { src: galleryAerial, alt: "Bad Ragaz", span: "col-span-2 row-span-1" },
    { src: galleryLobby, alt: "Lobby", span: "col-span-1 row-span-1" },
    { src: galleryEntrance, alt: "Entrance", span: "col-span-1 row-span-1" },
    { src: galleryRestaurant, alt: "Restaurant", span: "col-span-1 row-span-1" },
    { src: galleryBuffet, alt: "Breakfast", span: "col-span-1 row-span-1" },
    { src: galleryRiver, alt: "Tamina", span: "col-span-2 row-span-1" },
    { src: galleryTerrace, alt: "Terrace", span: "col-span-1 row-span-1" },
    { src: galleryPickup, alt: "Pickup", span: "col-span-1 row-span-1" },
    { src: galleryBalcony, alt: "Balcony", span: "col-span-2 row-span-1" },
  ];

  const milestones = [
    { icon: Calendar, value: "1918", label: t.about.founded },
    { icon: Heart, value: "100+", label: t.about.yearsHospitality },
  ];

  const locationHighlights = [
    { icon: Mountain, title: get("about_taminaschlucht") || t.about.taminaschlucht, description: get("about_taminaschlucht_desc") || t.about.taminaschluchtDesc },
    { icon: Droplets, title: get("about_thermal_source") || t.about.thermalSource, description: get("about_thermal_source_desc") || t.about.thermalSourceDesc },
    { icon: ThermometerSun, title: get("about_tamina_therme") || t.about.taminaTherme, description: get("about_tamina_therme_desc") || t.about.taminaThermeDesc },
    { icon: MapPin, title: get("about_central_location") || t.about.centralLocation, description: get("about_central_location_desc") || t.about.centralLocationDesc },
  ];

  const amenitiesData = [
    { icon: iconCabinet, title: get("about_modern_furnishing") || t.about.modernFurnishing, description: get("about_modern_furnishing_desc") || t.about.modernFurnishingDesc },
    { icon: iconBalcony, title: get("about_private_balconies") || t.about.privateBalconies, description: get("about_private_balconies_desc") || t.about.privateBalconiesDesc },
    { icon: iconCleaning, title: get("about_room_service") || t.about.roomService, description: get("about_room_service_desc") || t.about.roomServiceDesc },
    { icon: iconWifi, title: get("about_wifi") || t.about.wifiTitle, description: get("about_wifi_desc") || t.about.wifiDesc },
    { icon: iconSpa, title: get("about_wellness") || t.about.wellnessTitle, description: get("about_wellness_desc") || t.about.wellnessDesc },
    { icon: iconBathroom, title: get("about_products") || t.about.productsTitle, description: get("about_products_desc") || t.about.productsDesc },
  ];

  return (
    <>
      <section className="hotel-section bg-card overflow-hidden">
        <div className="hotel-container">
          <div className="grid lg:grid-cols-2 gap-8 lg:gap-20 items-center mb-12 md:mb-20">
            <div className="space-y-8 lg:order-1">
              <div className="space-y-3">
                <p className="hotel-caption text-accent">{get("about_story_caption") || t.about.storyCaption}</p>
                <h2 className="font-display text-3xl md:text-5xl lg:text-[3.5rem] font-light text-primary leading-[1.15] tracking-wide">
                  {get("about_story_heading1") || t.about.storyHeading1}<br />{get("about_story_heading2") || t.about.storyHeading2}<br /><span className="italic font-normal">{get("about_story_heading3") || t.about.storyHeading3}</span>
                </h2>
              </div>
              <div className="w-16 h-px bg-accent" />
              <p className="hotel-body text-muted-foreground max-w-lg">{get("about_story_text") || t.about.storyText}</p>
              <div className="flex gap-8 pt-4">
                {milestones.map((m) => (
                  <div key={m.label} className="text-center">
                    <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-2"><m.icon className="w-4 h-4 text-primary" strokeWidth={1.5} /></div>
                    <p className="font-display text-2xl md:text-3xl font-semibold text-primary">{m.value}</p>
                    <p className="font-body text-xs text-muted-foreground uppercase tracking-wider mt-1">{m.label}</p>
                  </div>
                ))}
              </div>
            </div>
            <div className="relative lg:order-2">
              <div className="relative">
                <div className="absolute -inset-3 md:-inset-4 border border-accent/30 rounded-lg" />
                <img src={hotelHistoric} alt="Hotel Quelle 1918" loading="lazy" className="w-full h-auto rounded-lg shadow-elevated relative z-10" />
              </div>
              <div className="mt-4 text-center"><p className="font-display text-sm text-muted-foreground italic">{get("about_historic_caption") || t.about.historicCaption}</p></div>
            </div>
          </div>
          <div className="relative mb-12 md:mb-20">
            <div className="bg-primary rounded-lg px-6 md:px-14 py-8 md:py-14 text-center relative overflow-hidden">
              <Quote className="absolute top-4 left-6 w-12 h-12 text-primary-foreground/10 rotate-180" />
              <Quote className="absolute bottom-4 right-6 w-12 h-12 text-primary-foreground/10" />
              <div className="relative z-10 max-w-3xl mx-auto space-y-4">
                <p className="font-display text-xl md:text-2xl text-primary-foreground font-light leading-relaxed italic">
                  {get("about_film_quote") || t.about.filmQuote}{" "}<span className="font-semibold not-italic">{t.about.filmTitle}</span>{" "}{get("about_film_quote2") || t.about.filmQuote2}
                </p>
                <div className="w-12 h-px bg-accent mx-auto" />
                <p className="font-body text-sm text-primary-foreground/80 uppercase tracking-widest">{get("about_film_welcome") || t.about.filmWelcome}</p>
              </div>
            </div>
          </div>
          <div>
            <div className="text-center max-w-3xl mx-auto mb-8 md:mb-12">
              <p className="hotel-caption text-accent mb-4">{get("about_location_caption") || t.about.locationCaption}</p>
              <h3 className="font-display text-3xl md:text-4xl font-light text-foreground leading-snug">
                {get("about_location_heading") || t.about.locationHeading}{" "}<span className="italic">{get("about_location_heading_italic") || t.about.locationHeadingItalic}</span>
              </h3>
            </div>
            <div className="grid sm:grid-cols-2 gap-4 md:gap-5">
              {locationHighlights.map((item) => (
                <div key={item.title} className="group relative bg-background rounded-lg p-5 md:p-7 shadow-soft hover:shadow-medium transition-all duration-300 border border-transparent hover:border-accent/20">
                  <div className="flex gap-5 items-start">
                    <div className="shrink-0 w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center group-hover:bg-primary/15 transition-colors duration-300"><item.icon className="w-5 h-5 text-primary" strokeWidth={1.5} /></div>
                    <div><h4 className="font-display text-xl font-semibold text-foreground mb-2">{item.title}</h4><p className="font-body text-sm text-muted-foreground leading-relaxed">{item.description}</p></div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>
      <section className="relative py-14 md:py-28 overflow-hidden">
        <img src={hotelExterior2} alt="" loading="lazy" className="absolute inset-0 w-full h-full object-cover" aria-hidden="true" />
        <div className="absolute inset-0 bg-primary/85" />
        <div className="hotel-container relative z-10">
          <h3 className="font-display text-xl md:text-3xl lg:text-4xl font-light text-primary-foreground leading-snug mb-8 md:mb-12 max-w-4xl">{get("about_amenities_heading") || t.about.amenitiesHeading}</h3>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
            {amenitiesData.map((amenity) => (
              <div key={amenity.title} className="bg-card/95 backdrop-blur-sm rounded-lg p-6 flex gap-5 items-start hover:bg-card transition-colors duration-300">
                <div className="shrink-0 w-12 h-12 rounded-md bg-primary/10 flex items-center justify-center"><img src={amenity.icon} alt={amenity.title} loading="lazy" className="w-6 h-6 brightness-0 invert-0 opacity-80" /></div>
                <div><h4 className="font-display text-lg font-semibold text-foreground mb-2">{amenity.title}</h4><p className="font-body text-sm text-muted-foreground leading-relaxed">{amenity.description}</p></div>
              </div>
            ))}
          </div>
        </div>
      </section>
      <section className="hotel-section bg-card">
        <div className="hotel-container">
          <div className="text-center max-w-3xl mx-auto mb-8 md:mb-12">
            <p className="hotel-caption text-accent mb-4">{get("about_gallery_caption") || t.about.galleryCaption}</p>
            <h3 className="font-display text-3xl font-semibold text-foreground">{get("about_gallery_heading") || t.about.galleryHeading}</h3>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4 auto-rows-[140px] md:auto-rows-[200px]">
            {galleryImages.map((image, index) => (
              <div key={index} className={`${image.span} relative overflow-hidden rounded-lg group cursor-pointer`}>
                <img src={image.src} alt={image.alt} loading="lazy" className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" />
                <div className="absolute inset-0 bg-foreground/0 group-hover:bg-foreground/20 transition-colors duration-300" />
              </div>
            ))}
          </div>
        </div>
      </section>
    </>
  );
};

export default About;
